import { Component, OnInit, TemplateRef, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AdminService } from '../services/admin/admin.service';
import { HelpersService } from '../../shared/servicio/helpers/helpers.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-medals',
  templateUrl: './medals.component.html',
  styleUrls: ['./medals.component.css']
})
export class MedalsComponent implements OnInit, OnDestroy {

  @ViewChild('createTemplate') createTemplate: TemplateRef<any>;
  @ViewChild('editTemplate') editTemplate: TemplateRef<any>;
  @ViewChild('deleteTemplate') deleteTemplate: TemplateRef<any>;
  @ViewChild('createFileInput') createFileInput: ElementRef<HTMLInputElement>;
  @ViewChild('editFileInput') editFileInput: ElementRef<HTMLInputElement>;

  // Modal
  modalRef: BsModalRef;

  // Form
  medalForm: FormGroup;
  submitted = false;

  // Loading y errores
  showLoading = true;
  preloader = false;
  div_error = 0;
  div_text_error = '';

  // Filtros
  searchTextFC = this.fb.control('');
  medalTypeFilterFC = this.fb.control('');

  // Estados de filtros mejorado - ahora es un solo filtro con 3 estados
  statusFilter: 'all' | 'active' | 'inactive' = 'all';

  // Datos
  list_medals: any[] = [];
  selectedMedal: any = null;

  // Paginación
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Filtros aplicados
  activeFilter: boolean | undefined = undefined;
  searchFilter: any;
  medalTypeFilter: any;

  // Imagen
  selectedImage: File | null = null;
  imagePreview: string | null = null;
  imageError = '';

  // Opciones de tipos de medalla - Platino ahora es morado
  medalTypeOptions = [
    { value: 'gold', label: 'Oro', color: '#FFD700', icon: 'fas fa-medal' },
    { value: 'silver', label: 'Plata', color: '#C0C0C0', icon: 'fas fa-medal' },
    { value: 'bronze', label: 'Bronce', color: '#CD7F32', icon: 'fas fa-medal' },
    { value: 'platinum', label: 'Platino', color: '#8A2BE2', icon: 'fas fa-medal' }, // Cambiado a morado
  ];

  // Destroy subject
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modalService: BsModalService,
    private adminService: AdminService,
    public helper: HelpersService
  ) {
    this.initForm();
    this.setupSearchFilters();
  }

  ngOnInit() {
    this.loadMedals();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Getter para acceso fácil a los controles del formulario
  get f() { return this.medalForm.controls; }

  /**
   * Inicializar formulario
   */
  initForm() {
    this.medalForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(500)]],
      medal_type: ['', [Validators.required]],
      referrals_required: ['', [Validators.required, Validators.min(1), Validators.max(999999)]],
      is_active: [true]
    });
  }

  /**
   * Configurar filtros de búsqueda con debounce
   */
  setupSearchFilters() {
    this.searchTextFC.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.searchFilter = value || '';
        this.currentPage = 1;
        this.loadMedals();
      });

    this.medalTypeFilterFC.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.medalTypeFilter = value || '';
        this.currentPage = 1;
        this.loadMedals();
      });
  }

  /**
   * Cargar medals
   */
  loadMedals() {
    this.showLoading = true;

    this.adminService.listMedals(
      this.currentPage,
      this.pageSize,
      this.activeFilter,
      this.medalTypeFilter,
      this.searchFilter
    ).subscribe({
      next: (response) => {
        this.list_medals = response || [];
        this.totalItems = response.count || 0;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        this.showLoading = false;
      },
      error: (error) => {
        console.error('Error loading medals:', error);
        this.showLoading = false;
        this.helper.modalError('Error al cargar las medallas');
      }
    });
  }

  /**
   * Toggle filtro de estado mejorado
   */
  toggleStatusFilter() {
    switch (this.statusFilter) {
      case 'all':
        this.statusFilter = 'active';
        this.activeFilter = true;
        break;
      case 'active':
        this.statusFilter = 'inactive';
        this.activeFilter = false;
        break;
      case 'inactive':
        this.statusFilter = 'all';
        this.activeFilter = undefined;
        break;
    }

    this.currentPage = 1;
    this.loadMedals();
  }

  /**
   * Obtener texto para el filtro de estado
   */
  getStatusFilterText(): string {
    switch (this.statusFilter) {
      case 'all':
        return 'TODOS';
      case 'active':
        return 'ACTIVOS';
      case 'inactive':
        return 'INACTIVOS';
      default:
        return 'TODOS';
    }
  }

  /**
   * Obtener icono para el filtro de estado
   */
  getStatusFilterIcon(): string {
    return 'assets/img/icons/icon-order-doctor-activated.png';
  }

  /**
   * Cambiar página
   */
  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadMedals();
    }
  }

  /**
   * Abrir modal crear
   */
  openCreateModal() {
    this.resetForm();
    this.selectedMedal = null;
    this.modalRef = this.modalService.show(this.createTemplate, {
      class: 'modal-lg',
      backdrop: 'static'
    });
  }

  /**
   * Abrir modal editar
   */
  openEditModal(medal: any) {
    this.resetForm();
    this.selectedMedal = medal;
    this.populateForm(medal);
    this.modalRef = this.modalService.show(this.editTemplate, {
      class: 'modal-lg',
      backdrop: 'static'
    });
  }

  /**
   * Abrir modal eliminar
   */
  openDeleteModal(medal: any) {
    this.selectedMedal = medal;
    this.modalRef = this.modalService.show(this.deleteTemplate, {
      backdrop: 'static'
    });
  }

  /**
   * Cerrar modal
   */
  closeModal() {
    this.modalRef.hide();
    this.resetForm();
  }

  /**
   * Reset formulario
   */
  resetForm() {
    this.medalForm.reset({
      is_active: true
    });
    this.submitted = false;
    this.div_error = 0;
    this.div_text_error = '';
    this.selectedImage = null;
    this.imagePreview = null;
    this.imageError = '';
  }

  /**
   * Poblar formulario con datos de la medalla
   */
  populateForm(medal: any) {
    this.medalForm.patchValue({
      name: medal.name,
      description: medal.description,
      medal_type: medal.medal_type,
      referrals_required: medal.referrals_required,
      is_active: medal.is_active
    });

    if (medal.image) {
      this.imagePreview = medal.image;
    }
  }

  /**
   * Submit formulario
   */
  onSubmit() {
    this.submitted = true;
    this.div_error = 0;

    if (this.medalForm.invalid) {
      return;
    }

    this.preloader = true;

    // Si hay una imagen seleccionada, la convertimos a base64
    if (this.selectedImage) {
      this.convertImageToBase64(this.selectedImage).then(base64Image => {
        this.sendMedalData(base64Image);
      }).catch(error => {
        this.preloader = false;
        this.div_error = 1;
        this.div_text_error = 'Error al procesar la imagen';
        console.error('Error converting image:', error);
      });
    } else {
      // Si no hay imagen nueva, enviamos la preview (que ya está en base64) o null
      this.sendMedalData(this.imagePreview || null);
    }
  }

  /**
   * Convertir imagen a base64
   */
  private convertImageToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Enviar datos de la medalla al servidor
   */
  private sendMedalData(imageBase64: string | null) {
    const medalData = {
      name: this.medalForm.value.name,
      description: this.medalForm.value.description,
      medal_type: this.medalForm.value.medal_type,
      referrals_required: this.medalForm.value.referrals_required,
      is_active: this.medalForm.value.is_active,
      image: imageBase64
    };

    const operation$ = this.selectedMedal
      ? this.adminService.updateMedal(this.selectedMedal.id, medalData)
      : this.adminService.createMedal(medalData);

    operation$.subscribe({
      next: (response) => {
        this.preloader = false;
        this.helper.modalSuccess(`¡Medalla ${this.selectedMedal ? 'actualizada' : 'creada'} exitosamente!`);
        this.closeModal();
        this.loadMedals();
      },
      error: (error) => {
        this.preloader = false;
        console.error('Error saving medal:', error);
        this.div_error = 1;
        this.div_text_error = error.error.message || 'Error al guardar la medalla';
        this.helper.modalError(`Error al ${this.selectedMedal ? 'actualizar' : 'crear'} la medalla`);
      }
    });
  }

  /**
   * Eliminar medalla
   */
  deleteMedal() {
    if (!this.selectedMedal) return;

    this.preloader = true;

    this.adminService.deleteMedal(this.selectedMedal.id).subscribe({
      next: () => {
        this.preloader = false;
        this.helper.modalSuccess('¡Medalla eliminada exitosamente!');
        this.closeModal();
        this.loadMedals();
      },
      error: (error) => {
        this.preloader = false;
        console.error('Error deleting medal:', error);
        this.helper.modalError('Error al eliminar la medalla');
      }
    });
  }

  /**
   * Toggle estado de la medalla
   */
  toggleMedalStatus(medal: any) {
    const newStatus = !medal.is_active;

    this.helper.modalConfirm(
      `¿Está seguro que desea ${newStatus ? 'activar' : 'desactivar'} esta medalla?`
    ).then((result) => {
      if (result.value) {
        this.adminService.toggleMedalStatus(medal.id, newStatus).subscribe({
          next: () => {
            medal.is_active = newStatus;
            this.helper.modalSuccess(`¡Medalla ${newStatus ? 'activada' : 'desactivada'} exitosamente!`);
          },
          error: (error) => {
            console.error('Error updating status:', error);
            this.helper.modalError('Error al actualizar el estado de la medalla');
          }
        });
      }
    });
  }

  /**
   * Manejo de selección de imagen mejorado
   */
  onImageSelected(event: any) {
    const file = event.target.files[0] || event.dataTransfer.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      this.imageError = 'Solo se permiten archivos de imagen';
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      this.imageError = 'La imagen debe ser menor a 2MB';
      return;
    }

    this.selectedImage = file;
    this.imageError = '';

    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = (e.target as FileReader).result as string;
    };
    reader.readAsDataURL(file);
  }

  /**
   * Trigger file input programmatically
   */
  triggerFileInput(type: 'create' | 'edit') {
    console.log(`Intentando abrir selector de archivos para: ${type}`);

    try {
      if (type === 'create') {
        console.log('CreateFileInput reference:', this.createFileInput);
        if (this.createFileInput && this.createFileInput.nativeElement) {
          console.log('Usando ViewChild para create');
          this.createFileInput.nativeElement.click();
        } else {
          // Fallback: buscar el elemento directamente
          console.log('Usando fallback para create');
          const fileInput = document.querySelector('#createFileInput') as HTMLInputElement;
          console.log('FileInput encontrado:', fileInput);
          if (fileInput) {
            fileInput.click();
          } else {
            throw new Error('No se pudo encontrar el input de archivo');
          }
        }
      } else if (type === 'edit') {
        console.log('EditFileInput reference:', this.editFileInput);
        if (this.editFileInput && this.editFileInput.nativeElement) {
          console.log('Usando ViewChild para edit');
          this.editFileInput.nativeElement.click();
        } else {
          // Fallback: buscar el elemento directamente
          console.log('Usando fallback para edit');
          const fileInput = document.querySelector('#editFileInput') as HTMLInputElement;
          console.log('FileInput encontrado:', fileInput);
          if (fileInput) {
            fileInput.click();
          } else {
            throw new Error('No se pudo encontrar el input de archivo');
          }
        }
      }
    } catch (error) {
      console.error('Error al abrir selector de archivos:', error);
      this.imageError = 'Error al abrir el selector de archivos. Revisa la consola para más detalles.';
    }
  }

  /**
   * Drag and drop handlers
   */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.style.borderColor = '#007bff';
    target.style.backgroundColor = '#e7f3ff';
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.style.borderColor = '#ddd';
    target.style.backgroundColor = '#f8f9fa';
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.currentTarget as HTMLElement;
    target.style.borderColor = '#ddd';
    target.style.backgroundColor = '#f8f9fa';

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      // Create a synthetic event object
      const syntheticEvent = {
        target: { files: files },
        dataTransfer: { files: files }
      };
      this.onImageSelected(syntheticEvent);
    }
  }

  /**
   * Remover imagen seleccionada
   */
  removeImage() {
    this.selectedImage = null;
    this.imagePreview = null;
    this.imageError = '';

    // Limpiar ambos inputs file usando ViewChild
    if (this.createFileInput.nativeElement) {
      this.createFileInput.nativeElement.value = '';
    }
    if (this.editFileInput.nativeElement) {
      this.editFileInput.nativeElement.value = '';
    }

    // Fallback: limpiar por ID
    const createInput = document.querySelector('#createFileInput') as HTMLInputElement;
    const editInput = document.querySelector('#editFileInput') as HTMLInputElement;

    if (createInput) createInput.value = '';
    if (editInput) editInput.value = '';
  }

  /**
   * Obtener información del tipo de medalla
   */
  getMedalTypeInfo(type: string): any {
    return this.medalTypeOptions.find(opt => opt.value === type) ||
      { label: type, color: '#666', icon: 'fas fa-medal' };
  }

  /**
   * Obtener clase CSS para el estado
   */
  getStatusClass(medal: any): string {
    return medal.is_active ? 'text-success' : 'text-danger';
  }

  /**
   * Formatear fecha
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  }

  /**
   * Formatear puntos como número
   */
  formatPoints(points: number): string {
    return new Intl.NumberFormat('es-ES').format(points);
  }
}
